import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Form from '@rjsf/shadcn';
import type {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFSchema,
  TemplatesType,
  UiSchema,
} from '@rjsf/utils';
import defaultValidator, { customizeValidator } from '@rjsf/validator-ajv8';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LanguageProvider,
  SearchProvider,
  StoreIdProvider,
  useLanguageSafe,
  useSearchSafe,
} from '../context';
import { OffersProvider, ProductsProvider, StoreConfigProvider, UpsellsProvider } from '../providers';
import { ArrayFieldItemTemplate } from '../templates/ArrayFieldItemTemplate';
import { GridObjectFieldTemplate } from '../templates/GridObjectFieldTemplate';
import { groupFieldsByTabAndSection, parseSchemaGrouping } from '../utils/schema-grouping';
import { FormToolbar } from './FormToolbar';

// Default templates to use across all forms
const defaultTemplates = {
  ObjectFieldTemplate: GridObjectFieldTemplate,
  ArrayFieldItemTemplate,
};

interface GroupedFormEditorProps {
  schema: RJSFSchema;
  uiSchema: UiSchema;
  formData: Record<string, unknown>;
  onChange: (data: any) => void;
  widgets?: RegistryWidgetsType;
  fields?: RegistryFieldsType;
  templates?: Partial<TemplatesType>;
  storeId?: string; // Optional - if provided, will use context providers
  onFieldFocus?: (fieldPath: string) => void; // Callback when a field is focused
  currentPagePath?: string; // Current page path for x-pages filtering
  availablePagePaths?: string[]; // Available page paths for inferring x-pages
}

/**
 * Wrapper component that conditionally provides data context
 * Only wraps with LanguageProvider if one doesn't already exist
 */
function DataProviders({
  children,
  storeId,
  availableLanguages,
}: {
  children: React.ReactNode;
  storeId?: string;
  availableLanguages?: string[];
}) {
  // Check if we're already inside a LanguageProvider
  const existingLanguageContext = useLanguageSafe();

  // If we already have a LanguageProvider, don't create a new one
  const contentWithLanguageProvider = existingLanguageContext ? (
    <SearchProvider>{children}</SearchProvider>
  ) : (
    <LanguageProvider availableLanguages={availableLanguages as any}>
      <SearchProvider>{children}</SearchProvider>
    </LanguageProvider>
  );

  // If no storeId, render with only StoreIdProvider
  if (!storeId) {
    return <StoreIdProvider storeId={storeId}>{contentWithLanguageProvider}</StoreIdProvider>;
  }

  // With storeId, wrap with all providers
  return (
    <StoreIdProvider storeId={storeId}>
      <StoreConfigProvider storeId={storeId}>
        <ProductsProvider storeId={storeId}>
          <OffersProvider storeId={storeId}>
            <UpsellsProvider storeId={storeId}>{contentWithLanguageProvider}</UpsellsProvider>
          </OffersProvider>
        </ProductsProvider>
      </StoreConfigProvider>
    </StoreIdProvider>
  );
}

/**
 * Threshold for using virtualization (number of fields)
 */
const VIRTUALIZATION_THRESHOLD = 10;

/**
 * Recursively count all nested fields in a schema
 */
function countNestedFields(schema: RJSFSchema): number {
  if (!schema || typeof schema !== 'object' || !schema.properties) {
    return 0;
  }

  let count = 0;

  for (const [, fieldSchema] of Object.entries(schema.properties)) {
    if (!fieldSchema || typeof fieldSchema !== 'object') continue;

    // Check if it's a translation field (should be counted as 1 field, not its language sub-fields)
    const isTranslationField = fieldSchema.format === 'translation';

    // If it's an object with properties BUT NOT a translation field, count its nested fields recursively
    if (fieldSchema.type === 'object' && fieldSchema.properties && !isTranslationField) {
      const nestedCount = countNestedFields(fieldSchema as RJSFSchema);
      count += nestedCount > 0 ? nestedCount : 1; // Count at least 1 if it's an object
    } else {
      // It's a leaf field (string, number, translation, etc.), count it
      count += 1;
    }
  }

  return count;
}

/**
 * Flatten nested schema into an array of field paths and their schemas
 */
interface FlattenedField {
  path: string[];
  fieldName: string;
  schema: any;
  uiSchema: any;
  displayTitle: string;
}

/**
 * Check if a field should be included based on x-pages filter
 */
function shouldIncludeFieldByXPages(fieldUiSchema: any, currentPagePath?: string): boolean {
  // If no x-pages filter, include the field
  if (!fieldUiSchema['x-pages']) {
    return true;
  }

  // If no current page path, exclude fields with x-pages filter
  if (!currentPagePath) {
    return false;
  }

  const xPages = fieldUiSchema['x-pages'];

  // x-pages can be a string or array of strings
  const pages = Array.isArray(xPages) ? xPages : [xPages];

  // Check if current page path matches any of the x-pages values
  return pages.some((page: string) => {
    // Support exact match and path parameter matching (e.g., /thankyou/:orderId matches /thankyou/123)
    if (page === currentPagePath) {
      return true;
    }

    // Check if page is a path pattern (contains :param) and matches current path
    if (page.includes(':')) {
      const pattern = page.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(currentPagePath);
    }

    return false;
  });
}

function flattenSchemaFields(
  schema: RJSFSchema,
  uiSchema: UiSchema,
  parentPath: string[] = [],
  currentPagePath?: string,
): FlattenedField[] {
  if (!schema || typeof schema !== 'object' || !schema.properties) {
    return [];
  }

  const fields: FlattenedField[] = [];

  for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
    if (!fieldSchema || typeof fieldSchema !== 'object') continue;

    const currentPath = [...parentPath, fieldName];
    const fieldUiSchema = uiSchema[fieldName] || {};

    // Filter by x-pages if specified
    if (!shouldIncludeFieldByXPages(fieldUiSchema, currentPagePath)) {
      continue; // Skip this field
    }

    const displayTitle = fieldSchema.title || fieldName;

    // Check if it's a translation field (object with format: 'translation')
    const isTranslationField =
      fieldSchema.format === 'translation' || fieldUiSchema['ui:field'] === 'translation';

    // If it's an object with properties (like sections, topBar, etc.) BUT NOT a translation field, recurse
    if (fieldSchema.type === 'object' && fieldSchema.properties && !isTranslationField) {
      // Add nested fields recursively
      const nestedFields = flattenSchemaFields(
        fieldSchema as RJSFSchema,
        fieldUiSchema,
        currentPath,
        currentPagePath,
      );
      fields.push(...nestedFields);
    } else {
      // It's a leaf field (translation, string, etc.)
      fields.push({
        path: currentPath,
        fieldName,
        schema: fieldSchema,
        uiSchema: fieldUiSchema,
        displayTitle,
      });
    }
  }

  return fields;
}

/**
 * Virtualized section form - renders large lists of fields efficiently
 * Only visible fields are rendered in the DOM
 */
const VirtualizedSectionForm = memo(
  ({
    sectionSchema,
    sectionUiSchema,
    formData,
    onChange,
    widgets,
    fields,
    templates,
    validator,
    currentPagePath,
    scrollToFieldPath,
  }: {
    sectionSchema: RJSFSchema;
    sectionUiSchema: UiSchema;
    formData: Record<string, unknown>;
    onChange: (data: any) => void;
    widgets?: RegistryWidgetsType;
    fields?: RegistryFieldsType;
    templates?: Partial<TemplatesType>;
    validator?: any;
    currentPagePath?: string;
    scrollToFieldPath?: string;
  }) => {
    const parentRef = useRef<HTMLDivElement>(null);

    // Get language context to trigger re-renders when language changes
    const languageContext = useLanguageSafe();
    const selectedLanguage = languageContext?.selectedLanguage;

    // Flatten all nested fields for virtualization
    const flattenedFields = flattenSchemaFields(sectionSchema, sectionUiSchema, [], currentPagePath);

    // Setup virtualizer
    const virtualizer = useVirtualizer({
      count: flattenedFields.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 80, // Estimated height per field in pixels
      overscan: 5, // Render 5 extra items above/below viewport
    });

    // Scroll to the target field when scrollToFieldPath is provided
    useEffect(() => {
      if (!scrollToFieldPath) return;
      const idx = flattenedFields.findIndex((f) => f.path.join('.') === scrollToFieldPath);
      if (idx >= 0) {
        virtualizer.scrollToIndex(idx, { align: 'center' });
      }
    }, [scrollToFieldPath, flattenedFields, virtualizer]);

    return (
      <div ref={parentRef} className="h-[600px] overflow-auto">
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem: any) => {
            const field = flattenedFields[virtualItem.index];
            if (!field) return null;

            // Create a nested schema structure to match the original path
            let miniSchema: RJSFSchema = {
              type: 'object',
              properties: {
                [field.fieldName]: field.schema,
              },
            };

            let miniUiSchema: UiSchema = {
              [field.fieldName]: field.uiSchema,
            };

            // Wrap in parent objects to maintain the nested path
            for (let i = field.path.length - 2; i >= 0; i--) {
              const parentFieldName = field.path[i];
              miniSchema = {
                type: 'object',
                properties: {
                  [parentFieldName]: miniSchema,
                },
              };
              miniUiSchema = {
                [parentFieldName]: {
                  ...miniUiSchema,
                  'ui:title': false, // Hide parent titles
                },
              };
            }

            return (
              <div
                key={`${field.path.join('.')}-${virtualItem.index}-${selectedLanguage || 'default'}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
              >
                <Form
                  key={`form-${field.path.join('.')}-${selectedLanguage || 'default'}`}
                  schema={miniSchema}
                  uiSchema={miniUiSchema}
                  formData={formData}
                  validator={validator || defaultValidator}
                  onChange={onChange}
                  widgets={widgets}
                  fields={fields}
                  templates={defaultTemplates}
                  liveValidate
                  showErrorList={false}
                >
                  <></>
                </Form>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

VirtualizedSectionForm.displayName = 'VirtualizedSectionForm';

/**
 * Lazy-loaded section form - only renders when section is expanded
 * Memoized to prevent unnecessary re-renders
 */
const LazySectionForm = memo(
  ({
    sectionSchema,
    sectionUiSchema,
    formData,
    onChange,
    widgets,
    fields,
    templates,
    validator,
    currentPagePath,
    scrollToFieldPath,
  }: {
    sectionSchema: RJSFSchema;
    sectionUiSchema: UiSchema;
    formData: Record<string, unknown>;
    onChange: (data: any) => void;
    widgets?: RegistryWidgetsType;
    fields?: RegistryFieldsType;
    templates?: Partial<TemplatesType>;
    validator?: any;
    currentPagePath?: string;
    scrollToFieldPath?: string;
  }) => {
    // Get language context to ensure re-renders when language changes
    const languageContext = useLanguageSafe();
    const selectedLanguage = languageContext?.selectedLanguage;

    // Count all nested fields recursively
    const fieldCount = countNestedFields(sectionSchema);

    // Use virtualization for large field lists
    if (fieldCount > VIRTUALIZATION_THRESHOLD) {
      return (
        <VirtualizedSectionForm
          sectionSchema={sectionSchema}
          sectionUiSchema={sectionUiSchema}
          formData={formData}
          onChange={onChange}
          widgets={widgets}
          fields={fields}
          templates={defaultTemplates}
          validator={validator}
          currentPagePath={currentPagePath}
          scrollToFieldPath={scrollToFieldPath}
        />
      );
    }

    // Use regular form for smaller lists
    return (
      <Form
        key={`form-${selectedLanguage || 'default'}`}
        schema={sectionSchema}
        uiSchema={sectionUiSchema}
        formData={formData}
        validator={validator || defaultValidator}
        onChange={onChange}
        widgets={widgets}
        fields={fields}
        templates={defaultTemplates}
        liveValidate
        showErrorList={false}
      >
        <></>
      </Form>
    );
  },
  (prevProps, nextProps) => {
    // Prevent re-renders when props haven't changed (context hooks handle language/search changes)
    return (
      prevProps.sectionSchema === nextProps.sectionSchema &&
      prevProps.sectionUiSchema === nextProps.sectionUiSchema &&
      prevProps.widgets === nextProps.widgets &&
      prevProps.fields === nextProps.fields &&
      prevProps.templates === nextProps.templates &&
      prevProps.validator === nextProps.validator &&
      prevProps.scrollToFieldPath === nextProps.scrollToFieldPath &&
      JSON.stringify(prevProps.formData) === JSON.stringify(nextProps.formData)
    );
  },
);

LazySectionForm.displayName = 'LazySectionForm';

/**
 * Helper function to recursively search through any value for the query string
 */
function searchInValue(value: any, query: string, visited = new WeakSet()): boolean {
  if (value === undefined || value === null) return false;

  // String values
  if (typeof value === 'string') {
    return value.toLowerCase().includes(query);
  }

  // Number values (convert to string)
  if (typeof value === 'number') {
    return String(value).toLowerCase().includes(query);
  }

  // Boolean values (convert to string)
  if (typeof value === 'boolean') {
    return String(value).toLowerCase().includes(query);
  }

  // Arrays - recursively search each element
  if (Array.isArray(value)) {
    return value.some((item) => searchInValue(item, query, visited));
  }

  // Objects - recursively search all properties
  if (typeof value === 'object') {
    // Avoid circular references
    if (visited.has(value)) return false;
    visited.add(value);

    try {
      // Get all keys including nested ones
      for (const key of Object.keys(value)) {
        // Check property name
        if (key.toLowerCase().includes(query)) return true;

        // Recursively check property value
        try {
          if (searchInValue(value[key], query, visited)) return true;
        } catch (e) {
          // Skip properties that throw errors
          continue;
        }
      }
    } catch (e) {
      return false;
    }
  }

  return false;
}

/**
 * Helper function to recursively search through schema properties
 */
function searchInSchema(fieldSchema: any, query: string): boolean {
  if (!fieldSchema || typeof fieldSchema !== 'object') return false;

  // Check title
  if (fieldSchema.title && typeof fieldSchema.title === 'string') {
    if (fieldSchema.title.toLowerCase().includes(query)) return true;
  }

  // Check description
  if (fieldSchema.description && typeof fieldSchema.description === 'string') {
    if (fieldSchema.description.toLowerCase().includes(query)) return true;
  }

  // Check enum values
  if (fieldSchema.enum && Array.isArray(fieldSchema.enum)) {
    if (fieldSchema.enum.some((v: any) => String(v).toLowerCase().includes(query))) {
      return true;
    }
  }

  // For object types, recursively check properties
  if (fieldSchema.type === 'object' && fieldSchema.properties) {
    return Object.entries(fieldSchema.properties).some(([propName, propSchema]: [string, any]) => {
      if (propName.toLowerCase().includes(query)) return true;
      return searchInSchema(propSchema, query);
    });
  }

  // For array types, check items schema
  if (fieldSchema.type === 'array' && fieldSchema.items) {
    return searchInSchema(fieldSchema.items, query);
  }

  return false;
}

/**
 * Helper function to check if a field matches the search query
 */
function fieldMatchesSearch(
  fieldName: string,
  fieldSchema: any,
  fieldValue: any,
  searchQuery: string,
): boolean {
  if (!searchQuery) return true;

  const query = searchQuery.toLowerCase().trim();
  if (!query) return true;

  try {
    // Check field name
    if (fieldName.toLowerCase().includes(query)) {
      return true;
    }

    // Recursively search through schema
    if (searchInSchema(fieldSchema, query)) {
      return true;
    }

    // Recursively search through value
    if (searchInValue(fieldValue, query)) {
      return true;
    }
  } catch (error) {
    // Silently handle search errors
    return false;
  }

  return false;
}

/**
 * Helper function to recursively filter schema properties based on search
 */
function filterSchemaBySearch(schema: any, formData: any, searchQuery: string, parentPath = ''): any {
  if (!searchQuery || !schema || typeof schema !== 'object') return schema;

  const query = searchQuery.toLowerCase().trim();

  // For object types with properties, recursively filter
  if (schema.type === 'object' && schema.properties) {
    const filteredProperties: any = {};
    let hasMatches = false;

    Object.entries(schema.properties).forEach(([propName, propSchema]: [string, any]) => {
      const propPath = parentPath ? `${parentPath}.${propName}` : propName;
      const propValue = formData?.[propName];

      // Check if this property or its children match
      const matches = fieldMatchesSearch(propName, propSchema, propValue, searchQuery);

      if (matches) {
        // Check if it's a translation field (should not recurse into language properties)
        const isTranslationField = propSchema.format === 'translation';

        // If it's an object with nested properties BUT NOT a translation field, recursively filter it too
        if (propSchema.type === 'object' && propSchema.properties && !isTranslationField) {
          const filtered = filterSchemaBySearch(propSchema, propValue, searchQuery, propPath);
          // Only add if the filtered result is not null
          if (filtered) {
            filteredProperties[propName] = filtered;
            hasMatches = true;
          }
        } else {
          // It's a leaf field (including translation fields) - add it as-is
          filteredProperties[propName] = propSchema;
          hasMatches = true;
        }
      }
    });

    if (hasMatches) {
      return {
        ...schema,
        properties: filteredProperties,
      };
    }

    return null;
  }

  return schema;
}

/**
 * Helper function to filter uiSchema based on filtered schema
 */
function filterUiSchemaBySchema(uiSchema: any, filteredSchema: any): any {
  if (!filteredSchema || !filteredSchema.properties) return uiSchema;

  const filteredUiSchema: any = {};

  // Copy over metadata fields (ui:* properties)
  Object.entries(uiSchema).forEach(([key, value]) => {
    if (key.startsWith('ui:')) {
      filteredUiSchema[key] = value;
    }
  });

  // Only include uiSchema for properties that exist in filtered schema
  Object.keys(filteredSchema.properties).forEach((propName) => {
    if (uiSchema[propName]) {
      const propSchema = filteredSchema.properties[propName];
      const propUiSchema = uiSchema[propName];

      // Check if propSchema exists and is valid
      if (!propSchema || typeof propSchema !== 'object') {
        filteredUiSchema[propName] = propUiSchema;
        return;
      }

      // If it's an object with nested properties, recursively filter uiSchema
      if (propSchema.type === 'object' && propSchema.properties && typeof propUiSchema === 'object') {
        filteredUiSchema[propName] = filterUiSchemaBySchema(propUiSchema, propSchema);
      } else {
        filteredUiSchema[propName] = propUiSchema;
      }
    }
  });

  return filteredUiSchema;
}

/**
 * Helper function to extract field path from RJSF input ID
 * RJSF generates IDs like "root_field1_field2" for nested paths like "field1.field2"
 * Also handles IDs like "root_field1__field2" (double underscore for nested) or "field1.field2"
 */
function extractFieldPathFromId(id: string): string | null {
  if (!id || typeof id !== 'string') return null;

  // Remove "root_" prefix if present
  let path = id.replace(/^root_/, '');

  // Handle double underscores (RJSF sometimes uses __ for nesting)
  path = path.replace(/__/g, '.');

  // Replace single underscores with dots to get the field path
  path = path.replace(/_/g, '.');

  // Remove any trailing array indices (e.g., ".0", ".1") but keep them if they're part of the field name
  // This regex removes trailing ".number" patterns
  path = path.replace(/\.(\d+)$/, '');

  // Clean up multiple consecutive dots
  path = path.replace(/\.+/g, '.');

  // Remove leading/trailing dots
  path = path.replace(/^\.+|\.+$/g, '');

  return path || null;
}

/**
 * Inner component that uses search context
 */
function GroupedFormEditorInner({
  schema,
  uiSchema,
  formData,
  onChange,
  widgets,
  fields,
  templates,
  storeId,
  grouping,
  fieldGroups,
  onFieldFocus,
  currentPagePath,
  availablePagePaths,
}: GroupedFormEditorProps & {
  grouping: ReturnType<typeof parseSchemaGrouping>;
  fieldGroups: ReturnType<typeof groupFieldsByTabAndSection>;
}) {
  const searchContext = useSearchSafe();
  const searchQuery = searchContext?.searchQuery || '';
  const [activeTab, setActiveTab] = useState<string>(grouping.tabs[0]?.id || 'default');
  const containerRef = useRef<HTMLDivElement>(null);
  const [externalFocusFieldPath, setExternalFocusFieldPath] = useState<string | null>(null);

  // Track the target section and leaf field path for scrolling virtualized sections into view
  const [scrollTargetSectionId, setScrollTargetSectionId] = useState<string | null>(null);
  const [scrollTargetFieldPath, setScrollTargetFieldPath] = useState<string | null>(null);

  // Handle field focus events
  useEffect(() => {
    if (!onFieldFocus || !containerRef.current) return;

    const handleFocus = (event: FocusEvent) => {
      let target = event.target as HTMLElement | null;
      if (!target) return;

      // Walk up the DOM tree to find an element that has an id, name or data-field-path.
      // This makes the focus handler work for custom widgets like Radix switches
      // that don't render native input elements.
      let el: HTMLElement | null = target;
      while (
        el &&
        !el.id &&
        !el.getAttribute('name') &&
        !el.getAttribute('data-field-path') &&
        el !== containerRef.current
      ) {
        el = el.parentElement;
      }

      if (!el) return;

      // 1. Try ID attribute
      const id = el.id || el.getAttribute('id');
      if (id) {
        const fieldPath = extractFieldPathFromId(id);
        if (fieldPath) {
          onFieldFocus(fieldPath);
          return;
        }
      }

      // 2. Try name attribute (some widgets might use name instead of id)
      const name = (el as HTMLInputElement).name || el.getAttribute('name');
      if (name) {
        const fieldPath = extractFieldPathFromId(name);
        if (fieldPath) {
          onFieldFocus(fieldPath);
          return;
        }
      }

      // 3. Try data attribute if available
      const dataPath = el.getAttribute('data-field-path');
      if (dataPath) {
        onFieldFocus(dataPath);
      }
    };

    const container = containerRef.current;
    container.addEventListener('focusin', handleFocus);

    return () => {
      container.removeEventListener('focusin', handleFocus);
    };
  }, [onFieldFocus]);

  // Custom validator to support 'translation' format without warnings
  const customValidator = useMemo(
    () =>
      customizeValidator({
        customFormats: {
          translation: () => true, // Always valid for translation format
        },
      }),
    [],
  );

  // Track which sections are expanded for lazy loading (must be before any returns)
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [tabExpandedSections, setTabExpandedSections] = useState<Record<string, string[]>>({});

  const handleTabAccordionChange = useCallback((tabId: string, values: string[]) => {
    setTabExpandedSections((prev) => ({ ...prev, [tabId]: values }));
  }, []);

  // Helper function to get nested schema by path (e.g., ['content', 'app'] or 'content.app')
  const getNestedSchema = useCallback(
    (path: string | string[]): RJSFSchema | null => {
      const pathArray = typeof path === 'string' ? path.split('.') : path;
      let current: any = schema;

      for (const key of pathArray) {
        if (current && current.properties && current.properties[key]) {
          current = current.properties[key];
        } else {
          return null;
        }
      }

      return current as RJSFSchema;
    },
    [schema],
  );

  // Helper function to get nested uiSchema by path
  const getNestedUiSchema = useCallback(
    (path: string | string[]): UiSchema => {
      const pathArray = typeof path === 'string' ? path.split('.') : path;
      let current: any = uiSchema;

      for (const key of pathArray) {
        if (current && current[key]) {
          current = current[key];
        } else {
          return {};
        }
      }

      return (current || {}) as UiSchema;
    },
    [uiSchema],
  );

  // Helper function to get nested formData by path
  const getNestedFormData = useCallback(
    (path: string | string[]) => {
      const pathArray = typeof path === 'string' ? path.split('.') : path;
      let current: any = formData;

      for (const key of pathArray) {
        if (current && typeof current === 'object' && !Array.isArray(current) && key in current) {
          current = current[key];
        } else {
          return undefined;
        }
      }

      return current;
    },
    [formData],
  );

  // Helper function to filter fields and their nested schemas based on search query
  const filterFields = useCallback(
    (fields: string[]) => {
      if (!searchQuery) return fields;

      const filtered = fields.filter((fieldPath) => {
        const fieldSchema = getNestedSchema(fieldPath);
        const fieldValue = getNestedFormData(fieldPath);
        // Use the last part of the path for matching (e.g., 'app' from 'content.app')
        const fieldName = fieldPath.split('.').pop() || fieldPath;
        return fieldMatchesSearch(fieldName, fieldSchema, fieldValue, searchQuery);
      });

      return filtered;
    },
    [searchQuery, getNestedSchema, getNestedFormData],
  );

  // Helper function to get filtered schema for nested fields (handles both top-level and nested paths)
  const getFilteredFieldSchema = useCallback(
    (fieldPath: string): RJSFSchema | null => {
      const fieldSchema = getNestedSchema(fieldPath);
      if (!fieldSchema) return null;

      if (!searchQuery) {
        return fieldSchema;
      }

      const fieldValue = getNestedFormData(fieldPath);

      // If it's an object with nested properties, filter it
      if (
        fieldSchema &&
        typeof fieldSchema === 'object' &&
        !Array.isArray(fieldSchema) &&
        fieldSchema.type === 'object' &&
        fieldSchema.properties
      ) {
        const filtered = filterSchemaBySearch(fieldSchema, fieldValue, searchQuery, fieldPath);
        return (filtered || fieldSchema) as RJSFSchema;
      }

      return fieldSchema;
    },
    [searchQuery, getNestedSchema, getNestedFormData],
  );

  // Auto-switch to first tab with results when search changes
  React.useEffect(() => {
    if (!searchQuery || grouping.tabs.length === 0) return;

    // Find first tab with visible results
    for (const tab of grouping.tabs) {
      const tabSections = grouping.sections.filter((section) => {
        if (section.tabId === tab.id) return true;
        if (fieldGroups[tab.id]?.[section.id]?.length > 0) return true;
        return false;
      });

      // Check if this tab has any visible fields after filtering
      const hasVisibleFields = tabSections.some((section) => {
        const sectionFields = filterFields(fieldGroups[tab.id]?.[section.id] || []);
        return sectionFields.length > 0;
      });

      if (hasVisibleFields) {
        setActiveTab(tab.id);
        break; // Stop at first tab with results
      }
    }
  }, [searchQuery, grouping.tabs, grouping.sections, fieldGroups, filterFields]);

  // Helper: normalize a single editor-id path (strip "config." prefix)
  const normalizeEditorPath = useCallback((raw: string): string => {
    return raw.startsWith('config.') ? raw.slice('config.'.length) : raw;
  }, []);

  // Helper: convert an array instance path to its schema path by stripping ".items.N" segments.
  // e.g. "sections.items.0.titleTrans" → "sections"
  // This is needed because templates use instance paths (with indices) but fieldGroups uses schema paths.
  const toSchemaPath = useCallback((instancePath: string): string => {
    return instancePath.replace(/\.items\.\d+/g, '');
  }, []);

  // Helper: check if a sectionId corresponds to a real accordion section
  const isRealSection = useCallback(
    (sectionId: string): boolean => {
      return grouping.sections.some((s) => s.id === sectionId);
    },
    [grouping.sections],
  );

  // Helper: given a raw match with a phantom section (e.g. "default"), walk up the
  // path hierarchy to find the ancestor field that lives in a real section.
  const resolveToRealSection = useCallback(
    (
      normalizedPath: string,
      tabId: string,
    ): { normalizedPath: string; tabId: string; sectionId: string } | null => {
      const parts = normalizedPath.split('.');
      // Walk from the immediate parent up to the root
      for (let i = parts.length - 1; i >= 1; i--) {
        const ancestorPath = parts.slice(0, i).join('.');
        const sections = fieldGroups[tabId];
        if (!sections) continue;
        for (const [sectionId, fieldPaths] of Object.entries(sections)) {
          if (fieldPaths.includes(ancestorPath) && isRealSection(sectionId)) {
            // Return the ancestor path — the field is rendered inside this ancestor's form
            return { normalizedPath: ancestorPath, tabId, sectionId };
          }
        }
      }
      return null;
    },
    [fieldGroups, isRealSection],
  );

  // Helper: try to find a path (direct or ancestor match) in fieldGroups, respecting real sections
  const findInFieldGroups = useCallback(
    (path: string): { normalizedPath: string; tabId: string; sectionId: string } | null => {
      // Direct match
      for (const [tabId, sections] of Object.entries(fieldGroups)) {
        for (const [sectionId, fieldPaths] of Object.entries(sections)) {
          if (fieldPaths.includes(path)) {
            if (isRealSection(sectionId)) {
              return { normalizedPath: path, tabId, sectionId };
            }
            const resolved = resolveToRealSection(path, tabId);
            if (resolved) return resolved;
          }
        }
      }

      // Ancestor match: editor-id points to a parent object while fieldGroups has leaf paths
      const prefix = path + '.';
      for (const [tabId, sections] of Object.entries(fieldGroups)) {
        for (const [sectionId, fieldPaths] of Object.entries(sections)) {
          const leafMatch = fieldPaths.find((fp) => fp.startsWith(prefix));
          if (leafMatch) {
            if (isRealSection(sectionId)) {
              return { normalizedPath: leafMatch, tabId, sectionId };
            }
            const resolved = resolveToRealSection(leafMatch, tabId);
            if (resolved) return resolved;
          }
        }
      }

      // Walk-up: the path itself isn't in fieldGroups, but an ancestor segment might be.
      // e.g. "sections.titleTrans" → try "sections" which is the actual array field in fieldGroups.
      const parts = path.split('.');
      for (let i = parts.length - 1; i >= 1; i--) {
        const ancestor = parts.slice(0, i).join('.');
        for (const [tabId, sections] of Object.entries(fieldGroups)) {
          for (const [sectionId, fieldPaths] of Object.entries(sections)) {
            if (fieldPaths.includes(ancestor)) {
              if (isRealSection(sectionId)) {
                return { normalizedPath: ancestor, tabId, sectionId };
              }
              const resolved = resolveToRealSection(ancestor, tabId);
              if (resolved) return resolved;
            }
          }
        }
      }

      return null;
    },
    [fieldGroups, isRealSection, resolveToRealSection],
  );

  // Helper: find the tab, section, and normalized path for a list of candidate editor-id paths
  const resolveFieldLocation = useCallback(
    (rawEditorId: string): { normalizedPath: string; tabId: string; sectionId: string } | null => {
      // editor-id can be space-separated (one element maps to multiple fields)
      const candidates = rawEditorId.trim().split(/\s+/);

      for (const candidate of candidates) {
        const normalized = normalizeEditorPath(candidate);

        // Try the instance path as-is first
        const direct = findInFieldGroups(normalized);
        if (direct) return direct;

        // If the path contains array instance segments (items.N), convert to schema path
        // e.g. "sections.items.0.titleTrans" → "sections.titleTrans" → then walk up to "sections"
        const schemaPath = toSchemaPath(normalized);
        if (schemaPath !== normalized) {
          const fromSchema = findInFieldGroups(schemaPath);
          if (fromSchema) return fromSchema;
        }
      }

      return null;
    },
    [normalizeEditorPath, findInFieldGroups, toSchemaPath],
  );

  // Listen for editor element click events coming from preview iframes (postMessage)
  // Uses a counter so repeated clicks on the same element still trigger focus.
  const [externalFocusTrigger, setExternalFocusTrigger] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as any;
      if (data?.type !== 'TAGADAPAY_EDITOR_ELEMENT_CLICK') return;

      const { editorId } = data;
      if (!editorId) return;

      setExternalFocusFieldPath(editorId);
      setExternalFocusTrigger((c) => c + 1);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Programmatic focus when a field path is provided (e.g. from preview iframe click)
  useEffect(() => {
    const focusFieldPath = externalFocusFieldPath;
    if (!focusFieldPath || !containerRef.current) return;

    const resolved = resolveFieldLocation(focusFieldPath);

    if (!resolved) {
      return;
    }

    const { normalizedPath, tabId: targetTabId, sectionId: targetSectionId } = resolved;

    if (targetTabId) {
      setActiveTab(targetTabId);
    }

    // Expand sections so the target field is rendered in the DOM before focusing.
    if (grouping.tabs.length === 0) {
      // No tabs → expand all sections
      setExpandedSections((prev) => {
        const allIds = grouping.sections.map((s) => s.id);
        const merged = new Set([...prev, ...allIds]);
        return Array.from(merged);
      });
    } else if (targetTabId) {
      // Tabs → expand the target section (and keep others open)
      setTabExpandedSections((prev) => {
        const current = prev[targetTabId] || [];
        const merged = new Set([...current, targetSectionId]);
        return {
          ...prev,
          [targetTabId]: Array.from(merged),
        };
      });
    }

    // Build all candidate field paths to try focusing.
    // The original clicked paths (from editor-id) may differ from the resolved ancestor,
    // so try the original leaf fields first, then fall back to the resolved path.
    const originalPaths = focusFieldPath
      .trim()
      .split(/\s+/)
      .map((p: string) => normalizeEditorPath(p));

    // Tell virtualized sections to scroll to the target field so it's rendered in the DOM.
    // The first originalPath is the leaf field the user clicked on.
    setScrollTargetSectionId(targetSectionId);
    setScrollTargetFieldPath(originalPaths[0] || normalizedPath);

    // After tabs/accordions have updated, try to focus the actual input.
    // Use a longer delay to account for lazy-rendered sections (LazySectionForm).
    const tryFocus = (attempt: number) => {
      const container = containerRef.current;
      if (!container) return;

      // Try original leaf paths first (best UX: focus the exact field clicked),
      // then fall back to the resolved ancestor path
      const pathsToTry = [...new Set([...originalPaths, normalizedPath])];
      const candidateIds: string[] = [];
      for (const path of pathsToTry) {
        const idBase = path.replace(/\./g, '_');
        candidateIds.push(
          path,
          `config.${path}`,
          idBase,
          `root_${idBase}`,
          `root_config_${idBase}`,
        );
      }

      for (const id of candidateIds) {
        const el = container.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
        if (el) {
          if (typeof el.focus === 'function') {
            el.focus();
          }
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          // Clear scroll target and focus path after successful focus
          setScrollTargetSectionId(null);
          setScrollTargetFieldPath(null);
          setExternalFocusFieldPath(null);
          return;
        }
      }

      // Retry: lazy sections may not have rendered yet or virtualizer hasn't scrolled
      if (attempt < 5) {
        retryId = window.setTimeout(() => tryFocus(attempt + 1), 150);
      } else {
        // Clean up scroll target and focus path after all retries exhausted
        setScrollTargetSectionId(null);
        setScrollTargetFieldPath(null);
        setExternalFocusFieldPath(null);
      }
    };

    let retryId: number | undefined;
    const timeoutId = window.setTimeout(() => tryFocus(0), 80);

    return () => {
      window.clearTimeout(timeoutId);
      if (retryId !== undefined) window.clearTimeout(retryId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fieldGroups/grouping are intentionally excluded: they are read inside the effect but should not re-trigger it (causes unwanted re-scroll on field edits)
  }, [externalFocusFieldPath, externalFocusTrigger]);

  // If no tabs defined, use simple accordion structure
  if (grouping.tabs.length === 0) {
    // If no sections defined either, render all fields in a single section
    if (grouping.sections.length === 0) {
      const allFields = schema.properties ? Object.keys(schema.properties) : [];
      const filteredAllFields = filterFields(allFields);

      if (filteredAllFields.length === 0) {
        return (
          <>
            <FormToolbar />
            <div ref={containerRef} className="px-4 py-3">
              <p className="text-muted-foreground text-sm">No configuration fields available</p>
            </div>
          </>
        );
      }

      return (
        <>
          <FormToolbar />
          <div ref={containerRef} className="px-4 py-3">
            <Form
              schema={schema}
              uiSchema={uiSchema}
              formData={formData}
              validator={customValidator}
              onChange={onChange}
              widgets={widgets}
              fields={fields}
              templates={{ ...defaultTemplates, ...templates }}
              liveValidate
              showErrorList={false}
            >
              <></>
            </Form>
          </div>
        </>
      );
    }

    return (
      <>
        <FormToolbar />
        <div ref={containerRef} className="px-4 py-3">
          <Accordion
            type="multiple"
            value={expandedSections}
            onValueChange={setExpandedSections}
            className="w-full space-y-2"
          >
            {grouping.sections.map((section) => {
              const sectionFields = filterFields(fieldGroups['default']?.[section.id] || []);
              if (sectionFields.length === 0) return null;

              // Build nested schema structure from field paths
              const buildNestedSchema = (fieldPath: string): [string[], any, any] => {
                const pathParts = fieldPath.split('.');
                const fieldSchema = getFilteredFieldSchema(fieldPath);
                const fieldUiSchema = getNestedUiSchema(fieldPath);

                // Remove title for object fields to avoid duplication with accordion title
                let finalSchema = fieldSchema;
                if (
                  fieldSchema &&
                  typeof fieldSchema === 'object' &&
                  !Array.isArray(fieldSchema) &&
                  fieldSchema.type === 'object'
                ) {
                  const { title, ...schemaWithoutTitle } = fieldSchema;
                  finalSchema = schemaWithoutTitle;
                }

                // Filter uiSchema if search is active
                let finalUiSchema = fieldUiSchema;
                if (
                  searchQuery &&
                  fieldSchema &&
                  typeof fieldSchema === 'object' &&
                  !Array.isArray(fieldSchema) &&
                  fieldSchema.type === 'object' &&
                  fieldSchema.properties
                ) {
                  finalUiSchema = filterUiSchemaBySchema(fieldUiSchema, fieldSchema);
                }

                // Hide title for object fields
                const isObjectField =
                  fieldSchema &&
                  typeof fieldSchema === 'object' &&
                  !Array.isArray(fieldSchema) &&
                  'type' in fieldSchema &&
                  fieldSchema.type === 'object';

                if (isObjectField) {
                  finalUiSchema = {
                    ...finalUiSchema,
                    'ui:title': false as any,
                    'ui:options': { ...finalUiSchema['ui:options'], hideTitle: true },
                  };
                }

                return [pathParts, finalSchema || { type: 'string' }, finalUiSchema];
              };

              // Build nested structure
              const sectionProperties: Record<string, any> = {};
              const sectionUiSchemaProps: Record<string, any> = {};

              sectionFields.forEach((fieldPath) => {
                const [pathParts, fieldSchema, fieldUiSchema] = buildNestedSchema(fieldPath);

                // Build nested structure
                let currentProps = sectionProperties;
                let currentUi = sectionUiSchemaProps;

                for (let i = 0; i < pathParts.length; i++) {
                  const part = pathParts[i];
                  const isLast = i === pathParts.length - 1;

                  if (isLast) {
                    currentProps[part] = fieldSchema;
                    currentUi[part] = fieldUiSchema;
                  } else {
                    if (!currentProps[part]) {
                      currentProps[part] = { type: 'object', properties: {} };
                    }
                    if (!currentUi[part]) {
                      currentUi[part] = {};
                    }
                    currentProps = currentProps[part].properties;
                    currentUi = currentUi[part];
                  }
                }
              });

              const sectionSchema: RJSFSchema = {
                type: 'object',
                properties: sectionProperties,
              };

              const sectionUiSchema: UiSchema = sectionUiSchemaProps;

              const isExpanded = expandedSections.includes(section.id);

              return (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  className="border-border bg-card hover:border-primary/20 data-[state=open]:border-primary/30 mb-2 rounded-lg border-2 shadow-sm transition-all hover:shadow-md data-[state=open]:shadow-md [&:last-child]:border-b-2"
                >
                  <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span>{section.title}</span>
                      <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-medium">
                        {sectionFields.length}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="border-border bg-muted/30 border-t-2 px-4 pb-3 pt-3">
                    {isExpanded && (
                      <LazySectionForm
                        sectionSchema={sectionSchema}
                        sectionUiSchema={sectionUiSchema}
                        formData={formData}
                        onChange={onChange}
                        widgets={widgets}
                        fields={fields}
                        templates={templates}
                        validator={customValidator}
                        currentPagePath={currentPagePath}
                        scrollToFieldPath={scrollTargetSectionId === section.id ? scrollTargetFieldPath ?? undefined : undefined}
                      />
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </>
    );
  }

  // Render with tabs
  const tabsCount = grouping.tabs.length;
  // Tailwind grid classes (can't be dynamic, so use mapping)
  const gridColsClass =
    tabsCount === 1
      ? 'grid-cols-1'
      : tabsCount === 2
        ? 'grid-cols-2'
        : tabsCount === 3
          ? 'grid-cols-3'
          : 'grid-cols-4';

  return (
    <>
      <FormToolbar />
      <div ref={containerRef} className="px-4 py-3">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Enhanced TabsList with better styling */}
          <TabsList
            className={`bg-muted border-border mb-3 grid h-auto w-full rounded-lg border-2 p-1 ${gridColsClass}`}
          >
            {grouping.tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:shadow-md"
              >
                {tab.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {grouping.tabs.map((tab) => {
            // Get sections for this tab: explicitly assigned or via field grouping
            const tabSections = grouping.sections.filter((section) => {
              // Section explicitly assigned to this tab
              if (section.tabId === tab.id) return true;
              // Section has fields assigned to this tab
              if (fieldGroups[tab.id]?.[section.id]?.length > 0) return true;
              return false;
            });

            if (tabSections.length === 0) {
              return (
                <TabsContent key={tab.id} value={tab.id} className="mt-0 px-0 py-3">
                  <div className="flex flex-col items-center justify-center py-8">
                    <p className="text-muted-foreground text-sm">No configuration fields in this tab</p>
                  </div>
                </TabsContent>
              );
            }

            return (
              <TabsContent key={tab.id} value={tab.id} className="mt-0 px-0 py-0">
                <Accordion
                  type="multiple"
                  value={tabExpandedSections[tab.id] || []}
                  onValueChange={(values) => handleTabAccordionChange(tab.id, values)}
                  className="w-full space-y-2"
                >
                  {tabSections.map((section) => {
                    const sectionFields = filterFields(fieldGroups[tab.id]?.[section.id] || []);
                    if (sectionFields.length === 0) return null;

                    // Build nested schema structure from field paths
                    const buildNestedSchema = (fieldPath: string): [string[], any, any] => {
                      const pathParts = fieldPath.split('.');
                      const fieldSchema = getFilteredFieldSchema(fieldPath);
                      const fieldUiSchema = getNestedUiSchema(fieldPath);

                      // Remove title for object fields to avoid duplication with accordion title
                      let finalSchema = fieldSchema;
                      if (
                        fieldSchema &&
                        typeof fieldSchema === 'object' &&
                        !Array.isArray(fieldSchema) &&
                        fieldSchema.type === 'object'
                      ) {
                        const { title, ...schemaWithoutTitle } = fieldSchema;
                        finalSchema = schemaWithoutTitle;
                      }

                      // Filter uiSchema if search is active
                      let finalUiSchema = fieldUiSchema;
                      if (
                        searchQuery &&
                        fieldSchema &&
                        typeof fieldSchema === 'object' &&
                        !Array.isArray(fieldSchema) &&
                        'type' in fieldSchema &&
                        fieldSchema.type === 'object' &&
                        fieldSchema.properties
                      ) {
                        finalUiSchema = filterUiSchemaBySchema(fieldUiSchema, fieldSchema);
                      }

                      // Hide title for object fields
                      const isObjectField =
                        fieldSchema &&
                        typeof fieldSchema === 'object' &&
                        !Array.isArray(fieldSchema) &&
                        'type' in fieldSchema &&
                        fieldSchema.type === 'object';

                      if (isObjectField) {
                        finalUiSchema = {
                          ...finalUiSchema,
                          'ui:title': false as any,
                          'ui:options': { ...finalUiSchema['ui:options'], hideTitle: true },
                        };
                      }

                      return [pathParts, finalSchema || { type: 'string' }, finalUiSchema];
                    };

                    // Build nested structure
                    const sectionProperties: Record<string, any> = {};
                    const sectionUiSchemaProps: Record<string, any> = {};

                    sectionFields.forEach((fieldPath) => {
                      const [pathParts, fieldSchema, fieldUiSchema] = buildNestedSchema(fieldPath);

                      // Build nested structure
                      let currentProps = sectionProperties;
                      let currentUi = sectionUiSchemaProps;

                      for (let i = 0; i < pathParts.length; i++) {
                        const part = pathParts[i];
                        const isLast = i === pathParts.length - 1;

                        if (isLast) {
                          currentProps[part] = fieldSchema;
                          currentUi[part] = fieldUiSchema;
                        } else {
                          if (!currentProps[part]) {
                            currentProps[part] = { type: 'object', properties: {} };
                          }
                          if (!currentUi[part]) {
                            currentUi[part] = {};
                          }
                          currentProps = currentProps[part].properties;
                          currentUi = currentUi[part];
                        }
                      }
                    });

                    const sectionSchema: RJSFSchema = {
                      type: 'object',
                      properties: sectionProperties,
                    };

                    const sectionUiSchema: UiSchema = sectionUiSchemaProps;

                    const isExpanded = (tabExpandedSections[tab.id] || []).includes(section.id);

                    return (
                      <AccordionItem
                        key={section.id}
                        value={section.id}
                        className="border-border bg-card hover:bg-accent/5 rounded-lg border transition-colors"
                      >
                        <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span>{section.title}</span>
                            <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-normal">
                              {sectionFields.length} {sectionFields.length === 1 ? 'field' : 'fields'}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="border-border border-t px-4 pb-3 pt-3">
                          {isExpanded && (
                            <LazySectionForm
                              sectionSchema={sectionSchema}
                              sectionUiSchema={sectionUiSchema}
                              formData={formData}
                              onChange={onChange}
                              widgets={widgets}
                              fields={fields}
                              templates={templates}
                              scrollToFieldPath={scrollTargetSectionId === section.id ? scrollTargetFieldPath ?? undefined : undefined}
                            />
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </>
  );
}

/**
 * Renders form with tabs and accordions based on schema grouping metadata.
 * Automatically manages data providers internally when storeId is provided.
 */
export function GroupedFormEditor({
  onFieldFocus,
  currentPagePath,
  availablePagePaths,
  ...props
}: GroupedFormEditorProps) {
  const grouping = parseSchemaGrouping(props.schema, props.uiSchema, currentPagePath, availablePagePaths);
  const fieldGroups = groupFieldsByTabAndSection(props.schema, grouping);

  // Extract available languages from schema or formData
  const availableLanguages = React.useMemo(() => {
    // Try to get from schema properties
    if (props.schema?.properties?.languages) {
      const languagesProperty = props.schema.properties.languages as any;
      if (languagesProperty.default && Array.isArray(languagesProperty.default)) {
        return languagesProperty.default;
      }
    }
    // Try to get from formData
    if (props.formData?.languages && Array.isArray(props.formData.languages)) {
      return props.formData.languages;
    }
    // Default fallback
    return ['en', 'fr'];
  }, [props.schema, props.formData]);

  return (
    <DataProviders storeId={props.storeId} availableLanguages={availableLanguages}>
      <GroupedFormEditorInner
        {...props}
        onFieldFocus={onFieldFocus}
        currentPagePath={currentPagePath}
        availablePagePaths={availablePagePaths}
        grouping={grouping}
        fieldGroups={fieldGroups}
      />
    </DataProviders>
  );
}
